import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock } from "lucide-react";
import { fadeUp, viewportOnce } from "@/lib/motion";

function etOffsetHours(date) {
  try {
    const s = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      timeZoneName: "shortOffset",
    })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName").value;
    const m = s.match(/GMT([+-]\d+)/);
    return m ? parseInt(m[1], 10) : -5;
  } catch {
    return -5;
  }
}

function makeETDate(y, mIdx, d, h) {
  const guess = new Date(Date.UTC(y, mIdx, d, h, 0, 0));
  const off = etOffsetHours(guess);
  return new Date(Date.UTC(y, mIdx, d, h - off, 0, 0));
}

function nextThursday7ET() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
  }).formatToParts(now);
  const y = +parts.find((p) => p.type === "year").value;
  const mo = +parts.find((p) => p.type === "month").value;
  const da = +parts.find((p) => p.type === "day").value;
  const wd = parts.find((p) => p.type === "weekday").value;
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const add = (4 - map[wd] + 7) % 7;
  let target = makeETDate(y, mo - 1, da + add, 19);
  if (target.getTime() <= now.getTime()) target = makeETDate(y, mo - 1, da + add + 7, 19);
  return target;
}

const Unit = ({ value, label }) => (
  <div className="flex flex-col items-center" data-testid={`countdown-${label.toLowerCase()}`}>
    <div className="flex h-16 w-14 items-center justify-center rounded-lg border border-gold/25 bg-ink-elevated/70 backdrop-blur-sm sm:h-20 sm:w-16 sm:rounded-xl md:h-28 md:w-24">
      <span className="font-serif text-3xl font-semibold tabular-nums text-cream sm:text-4xl md:text-6xl">
        {String(value).padStart(2, "0")}
      </span>
    </div>
    <span className="mt-2 text-[10px] uppercase tracking-[0.2em] text-cream/50 sm:tracking-[0.25em] md:text-xs">
      {label}
    </span>
  </div>
);

export const Countdown = ({ onRegister }) => {
  const [target] = useState(nextThursday7ET);
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });

  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, target.getTime() - Date.now());
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);

  const dateLabel = target.toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <section
      data-testid="countdown-section"
      className="relative border-y border-cream/10 bg-ink py-16 md:py-24"
    >
      <div className="absolute inset-0 spotlight" aria-hidden="true" />
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        className="container-tasha relative flex flex-col items-center text-center"
      >
        <span className="overline flex items-center gap-2">
          <CalendarClock size={15} /> Next Live Class
        </span>
        <h2 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-cream md:text-5xl">
          Doors open in
        </h2>

        <div className="mt-10 flex items-end gap-2 sm:gap-3 md:gap-6">
          <Unit value={t.d} label="Days" />
          <span className="pb-6 font-serif text-2xl text-gold sm:pb-7 sm:text-3xl md:pb-8 md:text-5xl">:</span>
          <Unit value={t.h} label="Hours" />
          <span className="pb-6 font-serif text-2xl text-gold sm:pb-7 sm:text-3xl md:pb-8 md:text-5xl">:</span>
          <Unit value={t.m} label="Minutes" />
          <span className="pb-6 font-serif text-2xl text-gold sm:pb-7 sm:text-3xl md:pb-8 md:text-5xl">:</span>
          <Unit value={t.s} label="Seconds" />
        </div>

        <p className="mt-8 font-sans text-base text-cream/70">
          <span className="text-gold">{dateLabel}</span> · 7:00 PM EST · Live on Zoom
        </p>
        <button onClick={onRegister} data-testid="countdown-register-btn" className="btn-gold mt-8">
          Reserve My Seat — It's Free
        </button>
      </motion.div>
    </section>
  );
};

export default Countdown;
